import { createSlice, createAsyncThunk } from "@reduxjs/toolkit";
import axios from "axios";

const BASE_URL = "https://api.dilanaslan.com/api";

// Tüm seansları çek (filtreleme ile)
export const fetchSessions = createAsyncThunk(
  "sessions/fetchSessions",
  async (filters = {}) => {
    const params = new URLSearchParams();
    Object.keys(filters).forEach((key) => {
      if (filters[key]) params.append(key, filters[key]);
    });

    const res = await axios.get(`${BASE_URL}/sessions?${params.toString()}`);
    return res.data;
  }
);

// Tek seans detayı çek
export const fetchSessionById = createAsyncThunk(
  "sessions/fetchSessionById",
  async (id) => {
    const res = await axios.get(`${BASE_URL}/sessions/${id}`);
    return res.data;
  }
);

// Yeni seans oluştur
export const createSession = createAsyncThunk(
  "sessions/createSession",
  async (sessionData, { rejectWithValue }) => {
    try {
      const res = await axios.post(`${BASE_URL}/sessions`, sessionData);
      return res.data;
    } catch (error) {
      return rejectWithValue(
        error.response?.data?.error || "Seans oluşturulamadı"
      );
    }
  }
);

// Seans güncelle
export const updateSession = createAsyncThunk(
  "sessions/updateSession",
  async ({ id, data }, { rejectWithValue }) => {
    try {
      const res = await axios.put(`${BASE_URL}/sessions/${id}`, data);
      return res.data;
    } catch (error) {
      return rejectWithValue(
        error.response?.data?.error || "Seans güncellenemedi"
      );
    }
  }
);

// Ödeme durumu güncelle
export const updateSessionPayment = createAsyncThunk(
  "sessions/updateSessionPayment",
  async ({ id, isPaid, paymentDate, paymentMethod }, { rejectWithValue }) => {
    try {
      const res = await axios.patch(`${BASE_URL}/sessions/${id}/payment`, {
        isPaid,
        paymentDate,
        paymentMethod,
      });
      return res.data;
    } catch (error) {
      return rejectWithValue(
        error.response?.data?.error || "Ödeme durumu güncellenemedi"
      );
    }
  }
);

// Seans sil
export const deleteSession = createAsyncThunk(
  "sessions/deleteSession",
  async (id) => {
    await axios.delete(`${BASE_URL}/sessions/${id}`);
    return id;
  }
);

// Haftalık program getir
export const fetchWeeklySchedule = createAsyncThunk(
  "sessions/fetchWeeklySchedule",
  async (weekStart) => {
    const params = weekStart ? `?weekStart=${weekStart}` : "";
    const res = await axios.get(
      `${BASE_URL}/sessions/weekly/schedule${params}`
    );
    return res.data;
  }
);

// Müşteriye özel haftalık program getir
export const fetchCustomerWeeklySchedule = createAsyncThunk(
  "sessions/fetchCustomerWeeklySchedule",
  async ({ customerId, weekStart }) => {
    const params = weekStart ? `?weekStart=${weekStart}` : "";
    const res = await axios.get(
      `${BASE_URL}/sessions/weekly/customer/${customerId}${params}`
    );
    return res.data;
  }
);

const sessionSlice = createSlice({
  name: "sessions",
  initialState: {
    items: [],
    selectedSession: null,
    weeklySchedule: null,
    loading: false,
    error: null,
  },
  reducers: {
    clearError: (state) => {
      state.error = null;
    },
    clearSelectedSession: (state) => {
      state.selectedSession = null;
    },
  },
  extraReducers: (builder) => {
    builder
      // Fetch All
      .addCase(fetchSessions.pending, (state) => {
        state.loading = true;
      })
      .addCase(fetchSessions.fulfilled, (state, action) => {
        state.loading = false;
        state.items = action.payload;
      })
      .addCase(fetchSessions.rejected, (state, action) => {
        state.loading = false;
        state.error = action.error.message;
      })

      // Fetch By ID
      .addCase(fetchSessionById.pending, (state) => {
        state.loading = true;
      })
      .addCase(fetchSessionById.fulfilled, (state, action) => {
        state.loading = false;
        state.selectedSession = action.payload;
      })
      .addCase(fetchSessionById.rejected, (state, action) => {
        state.loading = false;
        state.error = action.error.message;
      })

      // Create
      .addCase(createSession.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(createSession.fulfilled, (state, action) => {
        state.loading = false;
        state.items.unshift(action.payload);
      })
      .addCase(createSession.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      })

      // Update
      .addCase(updateSession.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(updateSession.fulfilled, (state, action) => {
        state.loading = false;
        state.items = state.items.map((session) =>
          session._id === action.payload._id ? action.payload : session
        );
        if (state.selectedSession?._id === action.payload._id) {
          state.selectedSession = action.payload;
        }
      })
      .addCase(updateSession.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      })

      // Update Payment
      .addCase(updateSessionPayment.fulfilled, (state, action) => {
        state.items = state.items.map((session) =>
          session._id === action.payload._id ? action.payload : session
        );
        if (state.selectedSession?._id === action.payload._id) {
          state.selectedSession = action.payload;
        }
      })

      // Delete
      .addCase(deleteSession.fulfilled, (state, action) => {
        state.items = state.items.filter((s) => s._id !== action.payload);
        if (state.selectedSession?._id === action.payload) {
          state.selectedSession = null;
        }
      })

      // Fetch Weekly Schedule
      .addCase(fetchWeeklySchedule.pending, (state) => {
        state.loading = true;
      })
      .addCase(fetchWeeklySchedule.fulfilled, (state, action) => {
        state.loading = false;
        state.weeklySchedule = action.payload;
      })
      .addCase(fetchWeeklySchedule.rejected, (state, action) => {
        state.loading = false;
        state.error = action.error.message;
      })

      // Fetch Customer Weekly Schedule
      .addCase(fetchCustomerWeeklySchedule.pending, (state) => {
        state.loading = true;
      })
      .addCase(fetchCustomerWeeklySchedule.fulfilled, (state, action) => {
        state.loading = false;
        state.weeklySchedule = action.payload;
      })
      .addCase(fetchCustomerWeeklySchedule.rejected, (state, action) => {
        state.loading = false;
        state.error = action.error.message;
      });
  },
});

export const { clearError, clearSelectedSession } = sessionSlice.actions;
export default sessionSlice.reducer;
